import { ArticleComponent } from "@components/Article/ArticleComponent";
import ArticlePageError from "@components/Article/ArticlePageError";
import type {
	ArticleDiffData,
	ArticlePageData,
	EditArticlePageData,
	MarkdownArticlePageData,
	ReadonlyArticlePageData,
} from "@core/SitePresenter/types/ArticlePage";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { useCtrlKeyLinkHandler } from "@core-ui/hooks/useCtrlKeyLinkHandler";
import useScrollToArticleAnchor from "@core-ui/hooks/useScrollToArticleAnchor";
import { useAutoFocusArticle } from "@ext/article/hooks/useAutoFocusArticle";

type ArticlePageDataMap = {
	edit: EditArticlePageData;
	read: ReadonlyArticlePageData;
	markdown: MarkdownArticlePageData;
	diff: ArticleDiffData;
};

export interface ArticleComponentProps<T extends ArticlePageData["mode"]> {
	data: ArticlePageDataMap[T];
	isReadOnly: boolean;
}

const Article = ({ data }: { data: ArticlePageData }) => {
	const pageDataContext = PageDataContextService.value;

	useCtrlKeyLinkHandler(); // For opening links in tauri
	useAutoFocusArticle();
	useScrollToArticleAnchor(data);

	if (!pageDataContext.isArticle) return null;

	if (data.pageError) return <ArticlePageError error={data.pageError} />;

	// The whole-file source view is where unreadable properties are repaired, so it is the one
	// place such an article is not read-only. A `pageError` never reaches this view: the markdown
	// mode shows the real article, and the stand-ins carry the error instead.
	const isRepairView = data.mode === "markdown" && Boolean(data.isWholeFile);
	const isReadOnly = pageDataContext.conf.isReadOnly || (Boolean(data.articleProps?.errorCode) && !isRepairView);

	return <ArticleComponent data={data} isReadOnly={isReadOnly} />;
};

export default Article;
